import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Notice, Spinner } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { api, ApiError } from '../../lib/api';
import { SearchAdd } from '../../components/SearchAdd';
import { AddOrg, OrgList } from './FrameworkOrgs';
import {
  addDomain,
  addIndicator,
  deleteDomain,
  deleteFramework,
  duplicateFramework,
  deleteIndicator,
  getFramework,
  reviewSubmission,
  updateDomain,
  updateFramework,
  updateIndicator,
  type Domain,
  type Indicator,
} from '../../lib/frameworks';
import '../Page.css';
import '../../components/Form.css';
import './Admin.css';
import { BackLink } from '../../components/BackLink';

/** How many organisations the framework page lists before "See all". */
const ORGS_SHOWN = 3;

/**
 * Build a framework: its areas and indicators, then publish it and give
 * organisations access. Edits save as you leave each field.
 */
export function AdminFrameworkEditPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fw = useQuery({ queryKey: ['admin-framework', id], queryFn: () => getFramework(id) });
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [newArea, setNewArea] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');
  /** Every edit saves straight away; this says so at the top of the page. */
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-framework', id] }),
      qc.invalidateQueries({ queryKey: ['admin-frameworks'] }),
      qc.invalidateQueries({ queryKey: ['admin-fw-submissions'] }),
      qc.invalidateQueries({ queryKey: ['admin-school'] }),
    ]);

  const run = async (fn: () => Promise<unknown>, success?: string) => {
    setMessage(null);
    setSaveState('saving');
    try {
      await fn();
      await refresh();
      setSaveState('saved');
      if (success) setMessage({ tone: 'success', text: success });
    } catch (err) {
      setSaveState('error');
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    }
  };

  // Organisation changes are confirmed inside that section, next to the click.
  const [orgNote, setOrgNote] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const orgDone = async (note: { tone: 'success' | 'error'; text: string } | null) => {
    setOrgNote(note);
    await refresh();
  };

  if (fw.isLoading) return <Spinner />;
  if (fw.isError || !fw.data) return <Notice tone="error">This framework isn&rsquo;t available.</Notice>;
  const f = fw.data;
  const published = f.status === 'approved';
  const library = !f.school_id;
  const indicatorCount = f.domains.reduce((n, d) => n + d.indicators.length, 0);
  const pending = f.status === 'pending';
  const sharedIds = new Set(f.sharedWithSchools.map((s) => s.schoolId));
  // Newest first from the API; the editor shows a few and links to the full list.
  const recentOrgs = f.sharedWithSchools.slice(0, ORGS_SHOWN);

  return (
    <div className="page fw-editor">
      <BackLink to="/admin/frameworks">Framework library</BackLink>
      <header className="fw-editor-head">
        <div className="page-head">
          <p className="eyebrow">
            {published ? 'Published' : pending ? 'Sent for review' : 'Draft'} · {f.domains.length}{' '}
            {f.domains.length === 1 ? 'area' : 'areas'} · {indicatorCount} {indicatorCount === 1 ? 'indicator' : 'indicators'}
          </p>
          <h1>{f.name}</h1>
        </div>
        <SaveState state={saveState} />
      </header>

      <div aria-live="polite">{message ? <Notice tone={message.tone}>{message.text}</Notice> : null}</div>

      {pending ? (
        <section className="panel form-section review-panel">
          <div>
            <h2>Sent for review</h2>
            <p className="muted">
              {f.submittedBy ? `${f.submittedBy.name}${f.submittedBy.schoolName ? ` at ${f.submittedBy.schoolName}` : ''} built this in the app.` : 'A school built this in the app.'}{' '}
              Check it below and tidy anything up. Approving publishes it and makes it their default; either way they&rsquo;re told on their phone.
            </p>
          </div>
          <div className="form-actions">
            <Button onClick={() => run(() => reviewSubmission(id, { decision: 'approve' }), 'Approved. It’s published and now their default.')} disabled={!indicatorCount}>
              Approve and publish
            </Button>
            <Button variant="ghost" onClick={() => setSendBackOpen(true)}>
              Send back with a note
            </Button>
          </div>
        </section>
      ) : null}

      <section className="panel form-section">
        <h2>About</h2>
        <SaveField label="Name" value={f.name} onSave={(v) => run(() => updateFramework(id, { name: v }))} required />
        <SaveField label="Description" value={f.description} onSave={(v) => run(() => updateFramework(id, { description: v }))} />
        <div className="form-actions">
          {pending ? null : published ? (
            library ? (
              <label className="agree">
                <input type="checkbox" checked={!!Number(f.listed)} onChange={(e) => run(() => updateFramework(id, { listed: e.target.checked }))} />
                <span>Offer it to every organisation (otherwise only where assigned)</span>
              </label>
            ) : null
          ) : (
            <Button onClick={() => run(() => updateFramework(id, { status: 'approved' }), 'Published. Give organisations access below.')} disabled={!indicatorCount}>
              Publish
            </Button>
          )}
          {!published && f.status !== 'pending' ? (
            <Button variant="ghost" onClick={() => setDeleteOpen(true)}>
              Delete draft
            </Button>
          ) : null}
          {indicatorCount ? (
            <Button variant="ghost" onClick={() => setDuplicateOpen(true)}>
              Duplicate for an organisation
            </Button>
          ) : null}
        </div>
        {!published && !indicatorCount ? <p className="faint">Add at least one area with an indicator to publish.</p> : null}
      </section>

      {published ? (
        <section className="panel form-section">
          <div>
            <h2>Organisations</h2>
            <p className="muted">Who can use this framework. Their default is what new lessons are scored against.</p>
          </div>
          {recentOrgs.length ? (
            <OrgList frameworkId={id} ownerSchoolId={f.school_id} rows={recentOrgs} onDone={orgDone} />
          ) : (
            <p className="faint">No organisations have it yet.</p>
          )}
          {f.sharedWithSchools.length > ORGS_SHOWN ? (
            <Link to={`/admin/frameworks/${id}/organisations`} className="see-all">
              +{f.sharedWithSchools.length - ORGS_SHOWN} more · See all {f.sharedWithSchools.length}
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          ) : null}
          <div aria-live="polite">{orgNote ? <Notice tone={orgNote.tone}>{orgNote.text}</Notice> : null}</div>
          <AddOrg frameworkId={id} exclude={sharedIds} onDone={orgDone} />
        </section>
      ) : null}

      <section className="page">
        <h2>Areas and indicators</h2>
        {f.domains.map((d, i) => (
          <AreaEditor
            key={d.id}
            frameworkId={id}
            domain={d}
            first={i === 0}
            last={i === f.domains.length - 1}
            onMove={(dir) => {
              const other = f.domains[i + dir];
              return run(async () => {
                await updateDomain(id, d.id, { sortOrder: other.sortOrder });
                await updateDomain(id, other.id, { sortOrder: d.sortOrder });
              });
            }}
            run={run}
          />
        ))}
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newArea.trim()) return;
            run(() => addDomain(id, newArea.trim())).then(() => setNewArea(''));
          }}
        >
          <Field id="fw-new-area" label="New area" placeholder="e.g. Questioning" value={newArea} onChange={(e) => setNewArea(e.target.value)} />
          <Button type="submit" variant="secondary" disabled={!newArea.trim()}>
            Add area
          </Button>
        </form>
      </section>



      <Dialog open={sendBackOpen} onClose={() => setSendBackOpen(false)} title="Send it back">
        <p className="muted">Say what needs changing. They see this note in the app and can edit and send it again.</p>
        <div className="field">
          <label htmlFor="fw-send-back">Note</label>
          <textarea id="fw-send-back" className="textarea" value={sendBackNote} onChange={(e) => setSendBackNote(e.target.value)} autoFocus />
        </div>
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setSendBackOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!sendBackNote.trim()}
            onClick={async () => {
              setSendBackOpen(false);
              await run(() => reviewSubmission(id, { decision: 'reject', reason: sendBackNote.trim() }), 'Sent back with your note.');
              setSendBackNote('');
            }}
          >
            Send back
          </Button>
        </div>
      </Dialog>

      <DuplicateDialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        frameworkId={id}
        frameworkName={f.name}
        onDone={(newId) => {
          setDuplicateOpen(false);
          navigate(`/admin/frameworks/${newId}`);
        }}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this draft?">
        <p className="muted">Its areas and indicators are deleted too. This can&rsquo;t be undone.</p>
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              setDeleteOpen(false);
              await run(() => deleteFramework(id));
              navigate('/admin/frameworks');
            }}
          >
            Delete draft
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

/** A text field that saves when you leave it (if it changed). */
function SaveField({
  label,
  value,
  onSave,
  required,
  multiline,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
  required?: boolean;
  multiline?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => {
    const t = v.trim();
    if (t === value.trim() || (required && !t)) {
      setV(value);
      return;
    }
    onSave(t);
  };
  const id = `f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${value.length}`;
  if (multiline) {
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <textarea id={id} className="textarea fw-textarea" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} />
      </div>
    );
  }
  return <Field id={id} label={label} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} />;
}

function AreaEditor({
  frameworkId,
  domain,
  first,
  last,
  onMove,
  run,
}: {
  frameworkId: string;
  domain: Domain;
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => Promise<unknown>;
  run: (fn: () => Promise<unknown>, success?: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  return (
    <section className="panel form-section fw-area">
      <div className="fw-area-head">
        <SaveField label="Area" value={domain.name} onSave={(v) => run(() => updateDomain(frameworkId, domain.id, { name: v }))} required />
        <div className="fw-row-actions">
          <Button variant="ghost" onClick={() => onMove(-1)} disabled={first} aria-label={`Move ${domain.name} up`}>
            ↑
          </Button>
          <Button variant="ghost" onClick={() => onMove(1)} disabled={last} aria-label={`Move ${domain.name} down`}>
            ↓
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm(`Delete “${domain.name}” and its indicators?`)) run(() => deleteDomain(frameworkId, domain.id));
            }}
          >
            Delete
          </Button>
        </div>
      </div>
      {domain.indicators.map((ind, i) => (
        <IndicatorEditor
          key={ind.id}
          frameworkId={frameworkId}
          domainId={domain.id}
          indicator={ind}
          first={i === 0}
          last={i === domain.indicators.length - 1}
          onMove={(dir) => {
            const other = domain.indicators[i + dir];
            return run(async () => {
              await updateIndicator(frameworkId, domain.id, ind.id, { sortOrder: other.sortOrder });
              await updateIndicator(frameworkId, domain.id, other.id, { sortOrder: ind.sortOrder });
            });
          }}
          run={run}
        />
      ))}
      <form
        className="fw-new-indicator"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          run(() => addIndicator(frameworkId, domain.id, name.trim(), description.trim())).then(() => {
            setName('');
            setDescription('');
          });
        }}
      >
        <Field id={`new-ind-${domain.id}`} label="New indicator" placeholder="e.g. Uses open questions" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="field">
          <label htmlFor={`new-ind-desc-${domain.id}`}>What it looks like in a lesson</label>
          <textarea id={`new-ind-desc-${domain.id}`} className="textarea fw-textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <Button type="submit" variant="secondary" disabled={!name.trim()}>
          Add indicator
        </Button>
      </form>
    </section>
  );
}

function IndicatorEditor({
  frameworkId,
  domainId,
  indicator,
  first,
  last,
  onMove,
  run,
}: {
  frameworkId: string;
  domainId: string;
  indicator: Indicator;
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => Promise<unknown>;
  run: (fn: () => Promise<unknown>, success?: string) => Promise<void>;
}) {
  return (
    <div className="fw-indicator">
      <SaveField
        label="Indicator"
        value={indicator.name}
        onSave={(v) => run(() => updateIndicator(frameworkId, domainId, indicator.id, { name: v }))}
        required
      />
      <SaveField
        label="What it looks like in a lesson"
        value={indicator.description}
        onSave={(v) => run(() => updateIndicator(frameworkId, domainId, indicator.id, { description: v }))}
        multiline
      />
      <div className="fw-row-actions">
        <Button variant="ghost" onClick={() => onMove(-1)} disabled={first} aria-label="Move up">
          ↑
        </Button>
        <Button variant="ghost" onClick={() => onMove(1)} disabled={last} aria-label="Move down">
          ↓
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            if (window.confirm(`Delete “${indicator.name}”?`)) run(() => deleteIndicator(frameworkId, domainId, indicator.id));
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

/** "Saving…" / "All changes saved": every edit saves straight away, and this says so. */
function SaveState({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'idle') return <span className="save-state">Changes save as you go</span>;
  if (state === 'saving') return <span className="save-state">Saving…</span>;
  if (state === 'error') return <span className="save-state save-state-error">Not saved. Try again</span>;
  return (
    <span className="save-state">
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      All changes saved
    </span>
  );
}

/**
 * "Duplicate for an organisation": pick who it's for, name the copy, and land
 * in the new draft to tweak it. Publishing it, then "Make default", puts it to use.
 */
function DuplicateDialog({
  open,
  onClose,
  frameworkId,
  frameworkName,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  frameworkId: string;
  frameworkName: string;
  onDone: (newId: string) => void;
}) {
  const [org, setOrg] = useState<{ id: string; title: string } | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOrg(null);
      setName('');
      setError(null);
    }
  }, [open]);

  const create = async () => {
    if (!org) return;
    setBusy(true);
    setError(null);
    try {
      const r = await duplicateFramework(frameworkId, org.id, name.trim() || undefined);
      onDone(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} title="Duplicate for an organisation">
      <p className="muted">
        Makes a copy of {frameworkName} that belongs to one organisation. You can change its areas and indicators without affecting anyone else, then
        publish it and make it their default.
      </p>
      {org ? (
        <div className="picked">
          <span>
            For <strong>{org.title}</strong>
          </span>
          <Button variant="ghost" onClick={() => setOrg(null)}>
            Change
          </Button>
        </div>
      ) : (
        <SearchAdd
          label="Which organisation is it for?"
          placeholder="Search by name or code"
          noun="organisation"
          load={async (q) =>
            (await api<{ data: { id: string; name: string; invite_code: string | null }[] }>(`/admin/schools?q=${encodeURIComponent(q)}&page=1&pageSize=12`)).data.map(
              (o) => ({ id: o.id, title: o.name, sub: o.invite_code }),
            )
          }
          onAdd={async (o) => {
            setOrg({ id: o.id, title: o.title });
            setName(`${frameworkName} (${o.title})`);
          }}
        />
      )}
      {org ? <Field id="fw-dup-name" label="Name of the copy" value={name} onChange={(e) => setName(e.target.value)} /> : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="dialog-actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={create} loading={busy} disabled={!org}>
          Duplicate and edit
        </Button>
      </div>
    </Dialog>
  );
}
